import React, { useState, useEffect } from 'react';
import { getDatabase, ref, push, set } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import './AccessControl.css';

function AccessControl({ 
    pageData, 
    onUpdatePageData, 
    products = [],
    loading = false 
}) {
    const [isOnlyForMembers, setIsOnlyForMembers] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [customerCount, setCustomerCount] = useState(0);

    const db = getDatabase();

    // Initialize state from pageData
    useEffect(() => {
        if (pageData) {
            setIsOnlyForMembers(pageData.isOnlyForMembers || false);
            setSelectedProductId(pageData.accessControlProductId || '');
            
            // Find selected product details
            if (pageData.accessControlProductId && products.length > 0) {
                const product = products.find(p => p.id === pageData.accessControlProductId);
                setSelectedProduct(product || null);
                
                // Load customer count for selected product
                if (product) {
                    loadCustomerCount(pageData.accessControlProductId);
                }
            }
        }
    }, [pageData, products]);

    // Load customer count for a product
    const loadCustomerCount = async (productId) => {
        try {
            // Counted server-side and scoped to the products this account owns.
            const getCount = httpsCallable(functions, 'getProductCustomerCount');
            const result = await getCount({ productId });
            const count = result?.data?.count || 0;
            setCustomerCount(count);
            console.log(`📊 AccessControl: Found ${count} customers for product ${productId}`);
        } catch (error) {
            console.error('Error loading customer count:', error);
            setCustomerCount(0);
        }
    };

    const handleToggleAccess = (enabled) => {
        setIsOnlyForMembers(enabled);
        
        const updatedPageData = {
            ...pageData,
            isOnlyForMembers: enabled,
            // Clear product selection if disabling access control
            ...(enabled ? {} : { 
                accessControlProductId: '',
                accessControlProductName: '' 
            })
        };

        // If disabling, also clear local state
        if (!enabled) {
            setSelectedProductId('');
            setSelectedProduct(null);
        }

        onUpdatePageData(updatedPageData);
    };

    const handleProductSelection = (productId) => {
        setSelectedProductId(productId);
        
        const product = products.find(p => p.id === productId);
        setSelectedProduct(product || null);
        
        // Load customer count for selected product
        if (product) {
            loadCustomerCount(productId);
        }
        
        const updatedPageData = {
            ...pageData,
            isOnlyForMembers: true, // Auto-enable when product is selected
            accessControlProductId: productId,
            accessControlProductName: product?.title || ''
        };

        onUpdatePageData(updatedPageData);
    };

    // Language Change Handler
    const handleLanguageChange = (language) => {
        const updatedPageData = {
            ...pageData,
            accessControlLanguage: language
        };

        onUpdatePageData(updatedPageData);
    };

    // Create test customer for development
    const createTestCustomer = async () => {
        if (!selectedProduct) return;
        
        try {
            const testPurchase = {
                productId: selectedProduct.id,
                providerId: selectedProduct.providerId,
                customerEmail: 'test@example.com',
                customerInfo: {
                    name: 'Test Customer',
                    email: 'test@example.com',
                    phone: '+49 123 456789'
                },
                paymentType: 'free',
                amount: 0,
                currency: 'EUR',
                status: 'active',
                purchaseDate: Date.now()
            };

            const purchaseRef = push(ref(db, 'purchases'));
            await set(purchaseRef, testPurchase);
            
            // Reload customer count
            loadCustomerCount(selectedProduct.id);
            
            alert('Test customer created! You can now test access with: test@example.com');
        } catch (error) {
            console.error('Error creating test customer:', error);
            alert('Error creating test customer: ' + error.message);
        }
    };

    if (loading) {
        return (
            <div className="access-control-loading">
                <div className="access-loading-spinner"></div>
                <p>Loading access settings...</p>
            </div>
        );
    }

    return (
        <div className="access-control-container">
            <div className="access-control-header">
                <div className="access-header-icon">
                    <i className="fas fa-lock"></i>
                </div>
                <div className="access-header-content">
                    <h3 className="access-title">Page Access Control</h3>
                    <p className="access-subtitle">
                        Restrict page access to customers who have purchased specific products
                    </p>
                </div>
            </div>

            {/* Toggle Switch */}
            <div className="access-toggle-section">
                <div className="access-toggle-wrapper">
                    <div className="access-toggle-info">
                        <h4 className="toggle-label">Members Only Page</h4>
                        <p className="toggle-description">
                            Only customers who purchased the selected product can access this page
                        </p>
                    </div>
                    <div className="access-toggle-switch">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={isOnlyForMembers}
                                onChange={(e) => handleToggleAccess(e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Product Selection */}
            {isOnlyForMembers && (
                <div className="access-product-section">
                    <div className="product-section-header">
                        <h4 className="product-section-title">
                            <i className="fas fa-shopping-bag"></i>
                            Select Required Product
                        </h4>
                        <p className="product-section-description">
                            Choose which product customers must have purchased to access this page
                        </p>
                    </div>

                    {products.length === 0 ? (
                        <div className="no-products-message">
                            <div className="no-products-icon">
                                <i className="fas fa-exclamation-circle"></i>
                            </div>
                            <h5>No Products Available</h5>
                            <p>You need to create products first to use access control.</p>
                            <button 
                                className="create-product-btn"
                                onClick={() => window.open('/sales-platform', '_blank')}
                            >
                                <i className="fas fa-plus"></i>
                                Create Your First Product
                            </button>
                        </div>
                    ) : (
                        <div className="product-selector">
                            <div className="product-dropdown">
                                <select
                                    value={selectedProductId}
                                    onChange={(e) => handleProductSelection(e.target.value)}
                                    className="product-select"
                                >
                                    <option value="">Select a product...</option>
                                    {products.map(product => (
                                        <option key={product.id} value={product.id}>
                                            {product.title} - €{product.price}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Selected Product Preview */}
                            {selectedProduct && (
                                <div className="selected-product-preview">
                                    <div className="product-preview-header">
                                        <h5>Selected Product</h5>
                                    </div>
                                    <div className="product-preview-card">
                                        <div className="product-preview-info">
                                            <h6 className="product-preview-title">
                                                {selectedProduct.title}
                                            </h6>
                                            <p className="product-preview-price">
                                                €{selectedProduct.price}
                                            </p>
                                            <p className="product-preview-type">
                                                {selectedProduct.subtype || 'Digital Product'}
                                            </p>
                                        </div>
                                        <div className="product-preview-stats">
                                            <div className="stat-item">
                                                <span className="stat-label">Customers</span>
                                                <span className="stat-value">
                                                    {customerCount}
                                                </span>
                                            </div>
                                            <button 
                                                className="test-customer-btn"
                                                onClick={createTestCustomer}
                                                title="Create test customer for development"
                                            >
                                                <i className="fas fa-plus"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Language Selection */}
            {isOnlyForMembers && (
                <div className="access-language-section">
                    <div className="language-section-header">
                        <h4 className="language-section-title">
                            <i className="fas fa-language"></i>
                            Access Page Language
                        </h4>
                        <p className="language-section-description">
                            Choose the language for the members-only verification page
                        </p>
                    </div>

                    <div className="language-selector">
                        <div className="language-options">
                            <button
                                className={`language-option ${(!pageData.accessControlLanguage || pageData.accessControlLanguage === 'en') ? 'active' : ''}`}
                                onClick={() => handleLanguageChange('en')}
                            >
                                <div className="language-flag">🇺🇸</div>
                                <div className="language-info">
                                    <span className="language-name">English</span>
                                    <span className="language-desc">International</span>
                                </div>
                            </button>
                            
                            <button
                                className={`language-option ${pageData.accessControlLanguage === 'de' ? 'active' : ''}`}
                                onClick={() => handleLanguageChange('de')}
                            >
                                <div className="language-flag">🇩🇪</div>
                                <div className="language-info">
                                    <span className="language-name">Deutsch</span>
                                    <span className="language-desc">German</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Access Status Preview */}
            {isOnlyForMembers && selectedProduct && (
                <div className="access-status-preview">
                    <div className="status-preview-header">
                        <i className="fas fa-shield-alt"></i>
                        <h4>Access Control Active</h4>
                    </div>
                    <div className="status-preview-content">
                        <div className="status-item">
                            <i className="fas fa-check-circle status-icon success"></i>
                            <span>Only customers who purchased "{selectedProduct.title}" can access this page ({customerCount} customers)</span>
                        </div>
                        <div className="status-item">
                            <i className="fas fa-user-shield status-icon info"></i>
                            <span>Email verification required for access</span>
                        </div>
                        <div className="status-item">
                            <i className="fas fa-eye-slash status-icon warning"></i>
                            <span>Page hidden from public visitors</span>
                        </div>
                        <div className="status-item">
                            <i className="fas fa-language status-icon info"></i>
                            <span>Language: {pageData.accessControlLanguage === 'de' ? 'German' : 'English'}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Help Section */}
            <div className="access-help-section">
                <div className="help-header">
                    <i className="fas fa-question-circle"></i>
                    <h5>How does it work?</h5>
                </div>
                <div className="help-content">
                    <div className="help-step">
                        <span className="step-number">1</span>
                        <p>Customer purchases your selected product</p>
                    </div>
                    <div className="help-step">
                        <span className="step-number">2</span>
                        <p>Their email is automatically saved to the product's customer list</p>
                    </div>
                    <div className="help-step">
                        <span className="step-number">3</span>
                        <p>When they visit this page, they'll be asked to verify their email</p>
                    </div>
                    <div className="help-step">
                        <span className="step-number">4</span>
                        <p>If verified, they get access - otherwise they see an access denied message</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AccessControl;
